import {
  IOrderLine,
  IOrdersPagination,
} from '../../../graphQL/queries/getOrders/types'
import { IOrderDetail } from '../../../graphQL/queries/order/types'

/**
 * Page size used for every `getOrders` request unless a caller overrides it.
 * The repo carries no global default to inherit.
 * @internal
 */
export const DEFAULT_ORDERS_PAGE_SIZE = 10

/**
 * How long to wait between poll ticks while an order sits in `PROCESSING`.
 *
 * The fallback, not the value: a storefront overrides it with
 * `configuration.orderConfirmation.pollingTime`, and this applies to every
 * storefront that has authored no such block — see
 * `utils/orderConfirmation.ts`.
 * @internal
 */
export const ORDER_POLL_INTERVAL_MS = 5000

/**
 * How long to keep polling before giving up.
 *
 * A client-side deadline, distinct from the server's own
 * `ExternalRefStatus.TIMEOUT` verdict. Both can happen and they mean different
 * things — see `pollTimedOut` below.
 *
 * The fallback for `configuration.orderConfirmation.timeout`, alongside
 * `ORDER_POLL_INTERVAL_MS`. There is no constant for `maxAttempts`: an
 * unconfigured poll is bounded by this deadline alone.
 * @internal
 */
export const ORDER_POLL_TIMEOUT_MS = 60000

/**
 * List-level fetch state.
 *
 * These four literals are the `OrderListStatus` contract published by
 * `OrderList` in `@engyalo/delivery-ui-components`. The union is kept flat on
 * purpose — see `isLoadingMore` on `IOrdersState` for why load-more does not
 * add a fifth member.
 * @internal
 */
export type OrdersStatus = 'idle' | 'loading' | 'ready' | 'error'

/**
 * Detail-level fetch state.
 *
 * A superset of `OrdersStatus`, because the detail screen has two outcomes the
 * list has no analogue for. `OrderDetailPanel` publishes both as members of its
 * own `OrderDetailStatus` and renders each with its own message, so they belong
 * in the status rather than in flags beside it — a separate boolean could only
 * be read by comparing it against the status anyway.
 *
 * `OrdersStatus` itself is deliberately not widened: it is `OrderList`'s
 * published `OrderListStatus` contract, and adding a member the component does
 * not know would be a lie about what the list can render.
 * @internal
 */
export type OrderDetailStatus = OrdersStatus | 'notFound' | 'sessionEnded'

/**
 * Where the in-webview order-creation flow stands.
 *
 * A separate union from `OrderDetailStatus`, not a widening of it, because the
 * two describe different things: that one is how a *read* of an existing order
 * went, this one is how far a *write* has got. An order that is `notFound` and
 * one that is `confirming` have no common scale to sit on.
 *
 * The three terminal members — `created`, `confirmed`, `failed` — are named to
 * match `OrderOutcome` in `utils/orderConfirmation.ts`, which is what derives
 * them, so the flow dispatches its verdict without a translation table. The
 * other three are properties of the flow rather than of the order:
 *
 * - `creating` — `createOrder` is in flight. No order exists yet, and nothing
 *   the shopper does can be undone by leaving.
 * - `confirming` — `confirmOrder` is in flight, or the poll is running. The
 *   order exists either way. One member covers both because the shopper is
 *   told the same thing and can do the same thing (nothing) in both.
 * - `unknown` — the poll spent its budget with the order still unsettled.
 *   Deliberately **not** `failed`: the order exists, `createOrder` returned it,
 *   and only the confirmation outcome is unknown. Announcing a failure here
 *   would tell a shopper their order did not happen when it very likely did.
 * @internal
 */
export type OrderCreationStatus =
  | 'idle'
  | 'creating'
  | 'confirming'
  | 'created'
  | 'confirmed'
  | 'failed'
  | 'unknown'

/**
 * Why a creation or confirmation attempt failed, for the screen to route on.
 *
 * Not an error object: the flow classifies the failure once, in its catch, and
 * the slice stores the classification. `message` is the server's own text
 * where it carries the reason a shopper needs — the client system's decline is
 * the case that matters — and `failedCheckoutRules` is the machine-readable
 * half, empty for everything that is not a rule rejection.
 * @internal
 */
export interface IOrderCreationFailure {
  message?: string
  failedCheckoutRules?: string[]
  /**
   * The order the verdict is about, where one exists.
   *
   * Carried because a failure is not always the absence of an order. A poll
   * tick that reads a configured failure status has the order in hand and it
   * is the one the screen must render — without this the slice would hold the
   * *previous* tick's copy, so `creationStatus: 'failed'` would sit beside a
   * `created.status` still reading `CREATED`. A creation failure omits it,
   * because nothing was created.
   */
  order?: IOrderDetail
}

/**
 * One history row, shaped for `OrderCard` / `OrderList`.
 *
 * Structurally the library's `OrderSummary`, declared here rather than
 * imported because the components are still unreleased. `itemsCount` is
 * derived client-side; `currency` is absent by design and read from
 * `config.options.currencyPrefix` at render time.
 * @internal
 */
export interface IOrderSummary {
  id: string
  createdAt?: string
  itemsCount?: number
  /**
   * The order's lines, kept so reorder can send them to `prefillCart` without
   * a second request. `OrderCard` and `OrderList` do not declare this prop
   * and ignore it.
   */
  items?: IOrderLine[]
  total?: number
  status?: string
  sequence?: number
}

export interface IOrdersState {
  list: IOrderSummary[]
  pagination: IOrdersPagination
  /**
   * Selected status filter. An empty string means no filter.
   */
  activeFilter: string
  status: OrdersStatus
  /**
   * True while a load-more page is in flight.
   *
   * `OrderList` computes `showRows = !isLoading && !isError && rows.length > 0`
   * and mounts the load-more sentinel only when `showRows` is true. Setting
   * `status: 'loading'` to fetch page 2 would therefore swap the rows for
   * skeletons and unmount the sentinel mid-scroll. So `status` holds `'ready'`
   * through pagination and the in-flight page is tracked here instead. This
   * doubles as the re-entrancy guard that stops a repeated sentinel fire from
   * requesting the same page twice.
   */
  isLoadingMore: boolean
  /**
   * Why the last load-more failed, or null.
   *
   * An append failure must not discard the pages already on screen, and
   * `status: 'error'` hides them. So a failed page 3 leaves `status: 'ready'`
   * and records the reason here for the container to surface as a toast or an
   * inline retry. Only a first-page failure sets `status: 'error'`.
   */
  loadMoreError: string | null
  /**
   * The order on the Detail screen, or null when none is loaded.
   *
   * The detail half is deliberately flat siblings of the list half rather than
   * a nested `selected: { ... }` namespace, so the slice reads with one
   * convention throughout. Each field below is the mirror of a list field, and
   * mirrors its reasoning as well as its name.
   */
  selected: IOrderDetail | null
  /**
   * Mirrors `status`, for the detail fetch, over the wider union.
   *
   * `'notFound'` is its own member rather than `'error'` plus a flag: the
   * server answers "no such order" and "not yours" with the same 404 on
   * purpose, so that a probe cannot confirm an id names a real order, and this
   * value inherits exactly that — "not available to you", nothing narrower.
   *
   * `'sessionEnded'` is the shopper's link having outlived its session. It is
   * set without a request going out; see the guard in `useGetOrderDetail`.
   */
  selectedStatus: OrderDetailStatus
  /**
   * True while a poll tick is in flight.
   *
   * The counterpart of `isLoadingMore` for display purposes only. Flipping
   * `selectedStatus` to `'loading'` every few seconds would blank an
   * already-rendered order and replace it with skeletons on every tick, so
   * `selectedStatus` holds `'ready'` throughout and the in-flight tick is
   * tracked here for a subtle pending affordance.
   *
   * Unlike `isLoadingMore` it is **not** a re-entrancy guard, and nothing
   * reads it to decide whether to fetch. Overlapping ticks are prevented in
   * `useGetOrderDetail` by the single-timer chain in `pollTimerRef`: a tick is
   * scheduled only once the previous one has resolved. Do not add a guard here
   * on the strength of the `isLoadingMore` analogy — the mechanisms differ.
   */
  isPollingOrder: boolean
  /**
   * Why the last poll tick failed, or null.
   *
   * The counterpart of `loadMoreError`: one failed tick must not discard the
   * order already on screen, and `selectedStatus: 'error'` would. So a failed
   * tick leaves `selectedStatus: 'ready'` and records the reason here. Only
   * the initial fetch sets `selectedStatus: 'error'`.
   */
  pollError: string | null
  /**
   * True once polling gave up on an order still reported as `PROCESSING`.
   *
   * A client-side deadline, and not the same fact as
   * `selected.externalRefStatus === 'TIMEOUT'`, which is the server's own
   * verdict about the downstream system. Either can occur without the other,
   * so they are two fields: one boolean could not say which happened.
   */
  pollTimedOut: boolean
  /**
   * Bumped once per refresh request from the Detail screen.
   *
   * A counter rather than a boolean because the value means nothing and only
   * its *change* is the signal: two refreshes in a row are two distinct edges,
   * where a flag already `true` would be none. Monotonic and never reset —
   * `clearOrderDetail` deliberately leaves it alone — since a reset is itself
   * a change and would read as a refresh to anything watching it.
   *
   * It exists because re-fetching is the one Detail action that cannot be
   * expressed as a plain reducer, and the usual seam does not work for it. An
   * `EventManager` handler that called `useGetOrderDetail` would hold a
   * *second instance* of that hook, whose poll timer and request token live in
   * `useRef` and are therefore per instance — so it would arm its own chain
   * against this slice rather than supersede the live one, both writing
   * `selected` on their own timers. That is COM-741's defect class, and the
   * escape used there does not extend to here: `backToHistory` could dispatch
   * `clearOrderDetail` because a reset *is* a reducer, and a fetch is not.
   *
   * A dispatch crosses instances where a ref does not, so the request is put
   * in the store and the one instance that owns the timer picks it up. See the
   * effect in `useGetOrderDetail`, which is also where the guard lives that
   * stops an instance which has fetched nothing from fetching on the edge.
   */
  detailRefreshNonce: number
  /**
   * How far the in-webview order-creation flow has got.
   *
   * The write half of this slice, kept here rather than in a slice of its own
   * because it is the same entity: the order the flow creates is the order the
   * poll re-reads and the screen renders, and `created` below is written from
   * both the mutation responses and `order` query ticks. A second slice would
   * have to mirror the read half to do that.
   *
   * `'idle'` for every storefront that has not authored
   * `options.inWebviewOrderConfirmation` — which is all of them until one
   * does, and is what makes the write half inert by default.
   */
  creationStatus: OrderCreationStatus
  /**
   * The order the flow created, or null before it exists.
   *
   * Distinct from `selected`, which is the order the *Detail screen* is
   * reading. Nothing stops a shopper reaching Order Detail later in the same
   * session, and folding the two together would mean opening a history row
   * overwrote the confirmation screen's order — or worse, that a stale
   * confirmation order rendered under a detail route.
   */
  created: IOrderDetail | null
  /**
   * The reason the flow failed, or null.
   *
   * Set only on the way into `'failed'`. The server's own message where it
   * carries the reason — a client-system decline says "order was not
   * confirmed" and that is the one thing worth showing — and null otherwise,
   * so the screen falls back to its configured copy rather than printing
   * `BAD REQUEST - …` at a shopper.
   */
  creationError: string | null
  /**
   * Checkout rules the order was rejected for, or empty.
   *
   * The `extras` of the server's `BAD_REQUEST`, filtered to members of the
   * `CheckoutRules` enum. Empty is the normal case and means "not a rule
   * rejection", which is why this is a list rather than a nullable one: the
   * screen asks whether there is anything to route to, and `[]` answers that
   * without a null check.
   *
   * Note the enforcement behind it is still fail-open in production until
   * COM-736 step 2 lands, so this is correct-but-rarely-populated today.
   */
  failedCheckoutRules: string[]
}

export interface OrdersPagePayload {
  orders: IOrderSummary[]
  pagination?: IOrdersPagination | null
}
